import { useState } from "react";
import { Avatar, Box, Button, Chip, MenuItem, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import NumericTextField from "components/NumericTextField";
import useGlobalContext from "hooks/globalData";
import { ILicensureBodySubjectAreaOption, ISubjectAreaOption } from "services/KnowfullyAdminClient";
import type { CreditLine, LineOverride } from "./CreditsSlot";
import type { ReferenceRow } from "./qualificationLinkState";
import { boardIsValid, boardTotal, sharedTotal } from "./wizardSave";

const styles = {
  boards: { display: "flex", flexWrap: "wrap", gap: 1, my: 1 },
  section: { border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, mt: 1 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 },
  row: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, alignItems: "end", mb: 1.5 },
  // Stacked below sm the two column titles would read as two more rows.
  headings: { display: { xs: "none", sm: "grid" }, gridTemplateColumns: "1fr 1fr", gap: 2, mb: 1.5 },
  board: { display: "flex", gap: 1 },
  amount: { width: 90, flexShrink: 0 },
  footer: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mt: 1 },
} as const;

let nextOverrideKey = 0;

type BoardOverridesProps = {
  entries: ReferenceRow[];
  // Overrides come from a board's vocabulary for one profession, so there is none to offer
  // until one is selected.
  professionId?: string;
  lines: CreditLine[];
  lineOptions?: ISubjectAreaOption[];
  // The boards whose override went with the last change to a shared line.
  dropped?: string[];
  onChange: (lines: CreditLine[]) => void;
};

export default function BoardOverrides({
  entries,
  professionId,
  lines,
  lineOptions = [],
  dropped = [],
  onChange,
}: BoardOverridesProps) {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const [boardId, setBoardId] = useState<string>();
  // A board opened with "Override credits for this board" before any of its lines has an area.
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [showNothing, setShowNothing] = useState(false);

  const { data: vocabulary = [], isError } = useQuery(
    ["skuService.getLicensureBodySubjectAreaOptions", professionId],
    async () => (await skuService.getLicensureBodySubjectAreaOptions(professionId!)).data ?? [],
    { enabled: !!professionId },
  );

  const boards = Array.from(
    new Map(entries.map((entry) => [entry.licensureBodyId, entry.lb])).entries(),
  ).map(([id, name]) => ({ id, name }));
  const nameOf = (id: string) => boards.find((candidate) => candidate.id === id)?.name ?? id;
  const optionsOf = (id: string): ILicensureBodySubjectAreaOption[] =>
    vocabulary.filter((option) => option.licensureBodyId === id);
  const withAreas = boards.filter((board) => optionsOf(board.id).length > 0);
  const nothing = boards.filter((board) => optionsOf(board.id).length === 0);
  const board = withAreas.find((candidate) => candidate.id === boardId);

  const shared = lines.filter((line) => !!line.subjectAreaId);
  const total = sharedTotal(lines);
  const overrideOf = (line: CreditLine, id: string) =>
    line.overrides?.find((override) => override.licensureBodyId === id);
  const countOf = (id: string) => shared.filter((line) => overrideOf(line, id)).length;
  const overridden = withAreas.filter((candidate) => countOf(candidate.id) > 0);

  // Replaces one board's override on one line and leaves every other board's alone.
  const setOverride = (line: CreditLine, id: string, next?: LineOverride) =>
    onChange(
      lines.map((candidate) =>
        candidate.key === line.key
          ? {
              ...candidate,
              overrides: [
                ...(candidate.overrides ?? []).filter((override) => override.licensureBodyId !== id),
                ...(next ? [next] : []),
              ],
            }
          : candidate,
      ),
    );

  const removeBoard = (id: string) => {
    setOpened((current) => new Set(Array.from(current).filter((other) => other !== id)));
    onChange(
      lines.map((line) => ({
        ...line,
        overrides: line.overrides?.filter((override) => override.licensureBodyId !== id),
      })),
    );
  };

  const notes = (
    <>
      {dropped.length > 0 && (
        <Typography variant="body2" color="warning.main" role="status">
          {t({ id: "theChangeRemovedOverridesFor" }, { boards: dropped.map(nameOf).join(", ") })}
        </Typography>
      )}
      {overridden.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t({ id: "overriddenFor" }, { boards: overridden.map((candidate) => candidate.name).join(", ") })}
        </Typography>
      )}
    </>
  );

  if (!professionId) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t({ id: "boardOverrides" })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t({ id: "selectOneProfessionToOverride" })}
        </Typography>
      </Box>
    );
  }

  const renderSection = (id: string) => {
    const areaName = (line: CreditLine) =>
      lineOptions.find((option) => option.subjectAreaId === line.subjectAreaId)?.name;
    const valid = boardIsValid(lines, id);

    if (!opened.has(id) && countOf(id) === 0) {
      return (
        <Box sx={styles.section}>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setOpened(new Set(opened).add(id))}>
            {t({ id: "overrideCreditsForThisBoard" })}
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={styles.section} role="group" aria-label={nameOf(id)}>
        <Box sx={styles.header}>
          <Typography variant="subtitle2">{nameOf(id)}</Typography>
          <Button size="small" color="error" onClick={() => removeBoard(id)}>
            {t({ id: "removeOverride" })}
          </Button>
        </Box>

        <Box sx={styles.headings}>
          <Typography variant="caption" color="text.secondary">
            {t({ id: "sharedCreditLine" })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t({ id: "forThisBoard" })}
          </Typography>
        </Box>

        {shared.map((line) => {
          const own = overrideOf(line, id);
          // A board area goes on one line at most, and a line the board leaves alone keeps its own area.
          const taken = shared
            .filter((other) => other.key !== line.key)
            .map((other) => overrideOf(other, id)?.subjectAreaId ?? other.subjectAreaId);
          const lineLabel = `${t({ id: "forThisBoard" })}, ${areaName(line)}`;

          return (
            <Box key={line.key} sx={styles.row}>
              <Typography variant="body2">
                {Number(line.credits).toFixed(2)} · {areaName(line)}
              </Typography>
              <Box sx={styles.board}>
                <Box sx={styles.amount}>
                  <NumericTextField
                    variant="standard"
                    fullWidth
                    step="0.25"
                    label={t({ id: "amountLabel" })}
                    inputProps={{ "aria-label": `${t({ id: "amountLabel" })}, ${lineLabel}` }}
                    disabled={!own}
                    defaultValue={own?.credits ?? line.credits}
                    // Shows what the board counts, its own amount or the shared one, whenever the user is not typing.
                    inputRef={(input: HTMLInputElement | null) => {
                      if (input && document.activeElement !== input) input.value = String(own?.credits ?? line.credits);
                    }}
                    onBlur={(e) => {
                      e.target.value = String(own?.credits ?? line.credits);
                    }}
                    onChange={(e) =>
                      own &&
                      setOverride(line, id, {
                        ...own,
                        credits: e.target.value === "" ? undefined : Number(e.target.value),
                      })
                    }
                  />
                </Box>
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  label={t({ id: "subjectAreaLabel" })}
                  // Shows "Keep the shared subject area" while the line has no board area.
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ "aria-label": lineLabel }}
                  value={own?.subjectAreaId ?? ""}
                  onChange={(e) =>
                    setOverride(
                      line,
                      id,
                      e.target.value
                        ? {
                            key: own?.key ?? `override-${(nextOverrideKey += 1)}`,
                            licensureBodyId: id,
                            subjectAreaId: e.target.value,
                            credits: own?.credits,
                          }
                        : undefined,
                    )
                  }
                >
                  <MenuItem value="">{t({ id: "keepTheSharedSubjectArea" })}</MenuItem>
                  {optionsOf(id)
                    .filter((option) => !taken.includes(option.subjectAreaId))
                    .map((option) => (
                      <MenuItem key={option.subjectAreaId} value={option.subjectAreaId}>
                        {option.boardLabel ?? option.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>
            </Box>
          );
        })}

        <Box sx={styles.footer}>
          <Typography variant="caption" color="text.secondary">
            {t({ id: "unchangedLinesKeepTheSharedAmountAndSubjectArea" })}
          </Typography>
          <Typography variant="body2" color={valid ? "text.primary" : "error"} role={valid ? undefined : "alert"}>
            {t({ id: "totalOfCredits" }, { total: boardTotal(lines, id).toFixed(2), shared: total.toFixed(2) })}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {t({ id: "boardOverrides" })}
      </Typography>
      {notes}

      {/* Without the vocabulary every board would read as having nothing to override. */}
      {isError && (
        <Typography variant="body2" color="error" role="alert">
          {t({ id: "fetchError" })}
        </Typography>
      )}

      <Typography variant="body2">{t({ id: "withTheirOwnSubjectAreas" })}</Typography>
      <Box sx={styles.boards}>
        {withAreas.map((candidate) => {
          const count = countOf(candidate.id);
          const invalid = count > 0 && !boardIsValid(lines, candidate.id);

          return (
            <Chip
              key={candidate.id}
              label={candidate.name}
              clickable
              variant={candidate.id === boardId ? "filled" : "outlined"}
              color={invalid ? "error" : count > 0 ? "primary" : "default"}
              avatar={count > 0 ? <Avatar>{count}</Avatar> : undefined}
              aria-label={[
                candidate.name,
                count > 0 && t({ id: "creditLinesOverridden" }, { count }),
                invalid && t({ id: "totalOfCredits" }, {
                  total: boardTotal(lines, candidate.id).toFixed(2),
                  shared: total.toFixed(2),
                }),
              ]
                .filter(Boolean)
                .join(", ")}
              aria-pressed={candidate.id === boardId}
              onClick={() => setBoardId(candidate.id === boardId ? undefined : candidate.id)}
            />
          );
        })}
      </Box>

      {!isError && nothing.length > 0 && (
        <>
          <Button size="small" aria-expanded={showNothing} onClick={() => setShowNothing(!showNothing)}>
            {t({ id: "nothingToOverride" })} ({nothing.length})
          </Button>
          {showNothing && (
            <Typography variant="body2" color="text.secondary">
              {nothing.map((candidate) => candidate.name).join(", ")}
            </Typography>
          )}
        </>
      )}

      {board && renderSection(board.id)}
    </Box>
  );
}
